import type { ElementInteractedProps } from './generated/element-interacted'
import type { ElementViewedProps } from './generated/element-viewed'
import type { FormActionProps } from './generated/form-action'
import type { LinkClickedProps } from './generated/link-clicked'
import type { LinkViewedProps } from './generated/link-viewed'

/** Stamped by useStandardTrack onto every standard track event. */
export type NwCommonTrackProps = Pick<
  FormActionProps,
  'deployable_name' | 'deployable_version' | 'pageview_id' | 'page_name' | 'page_url'
>

/** Full stamped payloads (post useStandardTrack). Page views use analytics.page() per PEDD — not here. */
export type NwStandardEventMap = {
  'Element Interacted': ElementInteractedProps
  'Element Viewed': ElementViewedProps
  'Form Action': FormActionProps
  'Link Clicked': LinkClickedProps
  'Link Viewed': LinkViewedProps
}

export const FORM_ACTION_SCHEMA_KEYS = [
  'additional_attributes',
  'bot_management',
  'deployable_name',
  'deployable_version',
  'event_id',
  'field',
  'field_type',
  'field_value',
  'form_action',
  'form_name',
  'page_name',
  'page_url',
  'pageview_id',
  'parent_event_id',
  'section_name',
  'step_name',
] as const

export const ELEMENT_VIEWED_SCHEMA_KEYS = [
  'additional_attributes',
  'bot_management',
  'deployable_name',
  'deployable_version',
  'entity_name',
  'event_id',
  'page_name',
  'page_url',
  'pageview_id',
] as const

export const ELEMENT_INTERACTED_SCHEMA_KEYS = [
  'additional_attributes',
  'bot_management',
  'deployable_name',
  'deployable_version',
  'entity_name',
  'event_id',
  'interaction_type',
  'item_copy',
  'item_position',
  'page_name',
  'page_url',
  'pageview_id',
  'parent_event_id',
  'section_name',
] as const

export const LINK_VIEWED_SCHEMA_KEYS = [
  'additional_attributes',
  'bot_management',
  'deployable_name',
  'deployable_version',
  'destination_url',
  'entity_name',
  'event_id',
  'link_copy',
  'page_name',
  'page_url',
  'pageview_id',
] as const

export const LINK_CLICKED_SCHEMA_KEYS = [
  'additional_attributes',
  'bot_management',
  'deployable_name',
  'deployable_version',
  'destination_url',
  'entity_name',
  'event_id',
  'link_copy',
  'page_name',
  'page_url',
  'pageview_id',
  'parent_event_id',
] as const

/** PEDD page-viewed.json top-level keys (PAGE call only — not useStandardTrack). */
export const PAGE_VIEWED_SCHEMA_KEYS = [
  'additional_attributes',
  'bot_management',
  'content_id',
  'content_source',
  'deployable_name',
  'deployable_version',
  'flow_name',
  'name',
  'pageview_id',
  'path',
  'referrer',
  'search',
  'title',
  'url',
] as const

/** Top-level keys kept for Experiment Bucketing Decision (extras nest under AA). */
export const EXPERIMENT_BUCKETING_SCHEMA_KEYS = [
  'additional_attributes',
  'bucketing_id',
  'bucketing_id_type',
  'deployable_name',
  'deployable_version',
  'experiment_key',
  'variation_key',
] as const

export const EVENT_SCHEMA_KEYS: Record<keyof NwStandardEventMap, readonly string[]> = {
  'Form Action': FORM_ACTION_SCHEMA_KEYS,
  'Element Viewed': ELEMENT_VIEWED_SCHEMA_KEYS,
  'Element Interacted': ELEMENT_INTERACTED_SCHEMA_KEYS,
  'Link Viewed': LINK_VIEWED_SCHEMA_KEYS,
  'Link Clicked': LINK_CLICKED_SCHEMA_KEYS,
}

export const EVENTS_REQUIRING_EVENT_ID = new Set<keyof NwStandardEventMap>([
  'Form Action',
  'Element Viewed',
  'Element Interacted',
  'Link Viewed',
  'Link Clicked',
])

export function buildStandardTrackPayload({
  eventName,
  deployableName,
  deployableVersion,
  pageviewId,
  pageName,
  pageUrl,
  props,
}: {
  eventName: keyof NwStandardEventMap
  deployableName: string
  deployableVersion: string
  pageviewId?: string
  pageName?: string
  pageUrl?: string
  props: Record<string, unknown>
}): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    deployable_name: deployableName,
    deployable_version: deployableVersion,
    ...props,
  }
  if (pageviewId != null) payload.pageview_id = pageviewId
  if (pageName != null) payload.page_name = pageName
  if (pageUrl != null) payload.page_url = pageUrl
  if (EVENTS_REQUIRING_EVENT_ID.has(eventName) && !payload.event_id) {
    payload.event_id = crypto.randomUUID()
  }
  return payload
}

/** Move keys that are not schema top-level into additional_attributes. */
export function nestNonSchemaProps(
  props: Record<string, unknown>,
  schemaKeys: readonly string[]
): Record<string, unknown> {
  const additional_attributes: Record<string, unknown> = {
    ...((props.additional_attributes as Record<string, unknown> | undefined) ?? {}),
  }
  const top: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(props)) {
    if (key === 'additional_attributes') continue
    if (schemaKeys.includes(key)) top[key] = value
    else additional_attributes[key] = value
  }
  if (Object.keys(additional_attributes).length > 0) {
    top.additional_attributes = additional_attributes
  }
  return top
}

export const NW_STANDARD_EVENTS = [
  'Element Interacted',
  'Element Viewed',
  'Form Action',
  'Link Clicked',
  'Link Viewed',
] as const satisfies ReadonlyArray<keyof NwStandardEventMap>

type CommonStampKeys = keyof NwCommonTrackProps

/**
 * Caller input: common stamps are filled by useStandardTrack.
 * event_id is optional — hook auto-stamps for view/lifecycle events.
 */
export type NwStandardTrackInputMap = {
  [E in keyof NwStandardEventMap]: Omit<NwStandardEventMap[E], CommonStampKeys | 'event_id'> &
    Partial<Pick<NwCommonTrackProps, 'page_name' | 'page_url'>> & {
      event_id?: string
    }
}
